/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable unicorn/prevent-abbreviations */
/* eslint-disable import/no-unresolved */

/* eslint-disable @typescript-eslint/no-shadow */

import { toIdFromHex } from '#~/api/index.js';
import { useDepot } from '#~/depot/hooks.js';
import { useLastRoomIdPref } from '#~/room/hooks.js';
import { Logo } from '#~/routes/scaffold/header/switcher/logo.js';
import { SwitcherLite } from '#~/routes/scaffold/header/switcher/switcher.js';
import { Button } from '@studio/design-system/button';
import { Footer } from '@studio/design-system/dialog';
import { Group } from '@studio/design-system/form';
import { Input } from '@studio/design-system/input';
import { Prompt } from '@studio/design-system/prompt';
import { normalizeId } from '@studio/models';
import type { Organization as OrgType } from '@studio/models/organization';
import type { Room as M_Room } from '@studio/models/room';
import { useLatestFn } from '@studio/react-utils/hooks';
import type { T_Maybe, T_Optional } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import React, { useEffect, useState } from 'react';
import { twMerge } from 'cn';
import { shallow } from 'zustand/shallow';
import { ActionBox, ButtonBox, Heading } from '../layout/index.js';
import { useStore } from '../store/hooks.js';
import { Panel } from '../types.js';
import { useWiring } from '../wiring.js';
import type { Props } from './types.js';

/** `<Profile>` panel. */
export const Organization = ({ setFooter, ...props }: Props): ReactElement => {
  const [dirtyKeys, setDirtyKeys] = useState<string[]>([]);
  const wiring = useWiring();

  const [rooms, organizations, produce] = useDepot(
    (state) => [state.roomRecords, state.organizations, state.produce],
    shallow
  );

  const [lastRoomID, _] = useLastRoomIdPref();
  const currentRoomId = normalizeId<M_Room['id']>(lastRoomID);
  const room = currentRoomId ? rooms[currentRoomId] : undefined;

  const [orgID, produceSettings] = useStore((µ) => [
    µ[Panel.Organization].overrideId ?? room?.organizationId,
    µ.produce
  ]);

  let org = orgID ? organizations[orgID] : undefined;

  // Auto-select organization with proper priority:
  // 1. Use overrideId if set (user manually selected)
  // 2. Use current room's organizationId (default to current studio's org)
  // 3. Fall back to first organization only if no room context exists
  useEffect(() => {
    // Only auto-select if no organization is currently selected
    if (!org && Object.keys(organizations).length > 0) {
      let targetOrgId: OrgType['id'] | undefined;

      // First priority: current room's organization
      if (room?.organizationId && organizations[room.organizationId]) {
        targetOrgId = room.organizationId;
      } else {
        // Fallback: first organization (only if no room context)
        targetOrgId = Object.keys(organizations)[0] as OrgType['id'];
      }

      if (targetOrgId && organizations[targetOrgId]) {
        produceSettings((draft) => {
          draft[Panel.Organization].overrideId = targetOrgId;
        });
      }
    }
  }, [org, organizations, produceSettings, room?.organizationId]);

  // Separate effect to handle room changes when user hasn't manually selected
  useEffect(() => {
    // Only update if we have a room organization and current selection doesn't match
    // AND the current selection is either the first org (auto-selected) or matches old room
    if (
      room?.organizationId &&
      organizations[room.organizationId] &&
      orgID !== room.organizationId &&
      Object.keys(organizations).length > 0
    ) {
      const firstOrgId = Object.keys(organizations)[0];
      // Only auto-switch if current selection appears to be auto-selected (first org)
      // or if there's no current selection
      if (!orgID || orgID === firstOrgId) {
        produceSettings((draft) => {
          draft[Panel.Organization].overrideId = room.organizationId;
        });
      }
    }
  }, [room?.organizationId, organizations, orgID, produceSettings]);

  const [name, setName] = useState(org?.name ?? '');
  const [website, setWebsite] = useState(org?.website ?? '');
  const [industry, setIndustry] = useState(org?.industry ?? '');
  const [photoUrl, setPhotoUrl] = useState(org?.photoUrl ?? '');

  // Update form fields when the selected organization changes
  useEffect(() => {
    if (org) {
      setName(org.name);
      setWebsite(org.website);
      setIndustry(org.industry);
      setPhotoUrl(org.photoUrl);
      setDirtyKeys([]); // Clear dirty keys when switching organizations
    }
  }, [org]);

  const [isShowingDeleteOrganizationPrompt, setIsShowingDeleteOrganizationPrompt] = useState(false);
  const [deleteOrganizationPromptInputText, setDeleteOrganizationPromptInputText] = useState('');
  const deleteKey = `Delete ${org?.name ?? ''}`;

  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleCommit = useLatestFn(async (overrides: T_Optional<OrgType> = {}): Promise<void> => {
    setIsSaving(true);

    const newOrg = {
      name: overrides.name ?? name,
      website: overrides.website ?? website,
      industry: overrides.industry ?? industry,
      photoUrl: overrides.photoUrl ?? photoUrl
    };

    await wiring.handleUpdateOrganization({
      id: toIdFromHex(org!.id)!,
      ...newOrg
    });

    produce((draft) => {
      draft.organizations[org!.id] = {
        ...draft.organizations[org!.id]!,
        id: org!.id,
        ...newOrg
      };
    });

    setDirtyKeys([]);
    setIsSaving(false);
  });

  const handleDirtyCheck =
    (key: string, current: T_Maybe<string>, setter: (next: string) => void) =>
    (next: string): void => {
      setter(next);

      if (next === current) {
        setDirtyKeys((keys: string[]) => keys.filter((k) => k !== key));

        return;
      }

      setDirtyKeys((keys: string[]) => {
        if (keys.includes(key)) {
          return keys;
        }

        return [...keys, key];
      });
    };

  const handleDeleteOrganization = useLatestFn((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    setDeleteOrganizationPromptInputText('');
    setIsShowingDeleteOrganizationPrompt(true);
  });

  const handleConfirmDeleteOrganization = useLatestFn(() => {
    if (deleteOrganizationPromptInputText !== deleteKey) {
      return;
    }

    if (!org) {
      return;
    }

    setIsDeleting(true);

    wiring.handleDeleteOrganization({
      id: toIdFromHex(org.id)!
    });
  });

  const handleOrgChange = useLatestFn((id: OrgType['id']): void => {
    const org = organizations[id];
    if (!org) {
      return;
    }

    setName(org.name);
    setWebsite(org.website);
    setIndustry(org.industry);
    setPhotoUrl(org.photoUrl);

    produceSettings((draft) => {
      draft[Panel.Organization].overrideId = org.id;
    });

    setDirtyKeys([]);
  });

  const uploadPhoto = useLatestFn((): void => {
    // click the hidden input to trigger the file upload dialog
    fileInputRef.current?.click();

    // listen for the file upload dialog to close
    fileInputRef.current?.addEventListener('change', async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];

      if (!file) {
        return;
      }

      setIsUploading(true);

      const url = await wiring.handleUploadOrganizationPhoto({
        id: toIdFromHex(org!.id)!,
        photo: file
      });

      setPhotoUrl(url);

      await handleCommit({ photoUrl: url });

      setIsUploading(false);

      fileInputRef.current!.value = '';
    });
  });

  const removePhoto = useLatestFn(async (): Promise<void> => {
    setIsRemoving(true);

    await wiring.handleDestoryOrganizationPhoto({
      id: toIdFromHex(org!.id)!
    });

    setPhotoUrl('');

    await handleCommit({ photoUrl: '' });

    setIsRemoving(false);
  });

  useEffect(() => {
    setFooter(
      <Footer>
        <ButtonBox className='flex flex-1'>
          <ButtonBox.Cancel onClick={wiring.handleClose} />
          <div className='flex-1' />
          <ButtonBox.Save
            disabled={dirtyKeys.length === 0 || isSaving}
            label={isSaving ? 'Saving...' : 'Save Changes'}
            onClick={(): void => {
              void handleCommit();
            }}
          />
        </ButtonBox>
      </Footer>
    );
  }, [dirtyKeys, handleCommit, isSaving, setFooter, wiring]);

  const canUpdate = org?.permissions.canUpdate ?? false;
  const canDestroy = org?.permissions.canDestroy ?? false;

  // Check if we're still loading organization data
  const isLoadingOrg = (room?.organizationId || Object.keys(organizations).length > 0) && !org;

  return (
    <div {...props} className={twMerge(props.className, 'flex h-full flex-col')}>
      <ActionBox>
        <Heading subtitle='Manage your Organization details' title='Organization Settings' />

        <ButtonBox>
          <div>
            <SwitcherLite
              className='rounded bg-base-100 py-10'
              dropdownAlign='end'
              dropdownClassName='min-w-[250px] mr-[-10px]'
              onOrganizationChange={handleOrgChange}
              selectedOrgId={org?.id}
            />
          </div>
        </ButtonBox>
      </ActionBox>

      <div className='w-[50%]'>
        <div className='my-32'>
          <div className='mb-32 flex flex-row'>
            <div className='mr-24 min-w-[80px]'>
              <Logo key={`logo-${name}-${photoUrl}`} name={name} photo={photoUrl} size='lg' />
            </div>
            <div>
              <div className='mb-8'>
                <h2 className='m-0 p-0 font-demibold'>Upload a Logo</h2>
                <p className='m-0 p-0 text-[12px] text-secondary'>PNG or JPG (max size: 3MB)</p>
              </div>

              <div>
                <div className='h-[1px] w-[1px] overflow-hidden opacity-0'>
                  <input accept='image/*' id='file' ref={fileInputRef} type='file' />
                </div>
                <div className='flex flex-row'>
                  <Button
                    className='mr-[8px] border-white text-white'
                    disabled={isUploading || isRemoving || !canUpdate || isLoadingOrg}
                    key={`add-upload-${photoUrl}-${isUploading ? 'u' : 'n'}-${
                      isRemoving ? 'r' : 'n'
                    }`}
                    onClick={(): void => {
                      uploadPhoto();
                    }}
                    variant='tertiary'
                  >
                    {isUploading ? 'Uploading...' : `${photoUrl ? 'Update' : 'Upload'} Logo`}
                  </Button>
                  {!!photoUrl && (
                    <Button
                      className='mr-[8px] border-white text-white'
                      disabled={isUploading || isRemoving || !canUpdate || isLoadingOrg}
                      key={`remove-upload-${photoUrl}-${isUploading ? 'u' : 'n'}-${
                        isRemoving ? 'r' : 'n'
                      }`}
                      onClick={(): void => {
                        void removePhoto();
                      }}
                      variant='tertiary'
                    >
                      {isRemoving ? 'Removing...' : 'Remove Logo'}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
          <hr />
        </div>

        <div className='my-16 flex flex-col gap-32'>
          <Group label='Organization Name'>
            <Input
              disabled={!canUpdate || isLoadingOrg}
              onChange={handleDirtyCheck('name', name, setName)}
              placeholder={isLoadingOrg ? 'Loading organization...' : 'Organization Name'}
              value={name}
            />
          </Group>

          <Group label='Website'>
            <Input
              disabled={!canUpdate || isLoadingOrg}
              onChange={handleDirtyCheck('website', website, setWebsite)}
              placeholder='Website'
              value={website}
            />
          </Group>

          <Group label='Industry'>
            <Input
              disabled={!canUpdate || isLoadingOrg}
              onChange={handleDirtyCheck('industry', industry, setIndustry)}
              placeholder='Industry'
              value={industry}
            />
          </Group>

          {!!canDestroy && (
            <a
              className={twMerge(isDeleting && 'text-muted')}
              href='#'
              onClick={handleDeleteOrganization}
            >
              {isDeleting ? 'Deleting' : 'Delete'} Organization
            </a>
          )}

          {!!isShowingDeleteOrganizationPrompt && (
            <Prompt
              className='min-w-[450px]'
              message={
                <div className='text-[14px] font-[400] text-secondary'>
                  <p>Are you sure you want to delete this organization?</p>
                  <p className='mb-24'>
                    Type <strong>{deleteKey}</strong> to confirm.
                  </p>
                  <Input
                    className={twMerge(
                      deleteOrganizationPromptInputText !== deleteKey && 'border border-error'
                    )}
                    focusOnMount
                    onChange={(v): void => {
                      setDeleteOrganizationPromptInputText(v);
                    }}
                    type='text'
                    value={deleteOrganizationPromptInputText}
                  />
                  <Button
                    className='mx-auto mt-40 bg-error'
                    disabled={deleteOrganizationPromptInputText !== deleteKey}
                    onClick={handleConfirmDeleteOrganization}
                    size='lg'
                  >
                    Delete Organization
                  </Button>
                </div>
              }
              onOpenChange={(next): void => {
                if (!next) {
                  setIsShowingDeleteOrganizationPrompt(false);
                }
              }}
              title={<h1 className='mb-0 text-[24px] font-demibold'>Delete {org?.name ?? ''}?</h1>}
            />
          )}
        </div>
      </div>
    </div>
  );
};
